import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Employee, EmployeeEvaluation, CriteriaRating, RatingLevel, ratingOptions } from "@/types/employee";
import { Criteria, CriteriaType, getDefaultCriteria, getBaselinePoints, getMinPoints, getMaxPoints, getWeightedScore, getWeightedMaxScore } from "@/types/criteria";
import { loadGlobalCriteria } from "@/lib/criteriaLoader";
import { PerformanceThreshold, getDefaultThresholds, getPerformanceLevelFromThresholds } from "@/types/performanceThresholds";
import { Clock, FileText, Ruler, ThumbsUp, Calendar, FolderCheck, Calculator, MessageSquare, HeartPulse, Activity, ListChecks, Wrench, Car, CheckCircle2, Info, Pencil, X, Check, Download, TrendingUp, Star, RotateCcw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Save } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DualScrollbar } from "@/components/ui/dual-scrollbar";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import BidirectionalSlider from "@/components/BidirectionalSlider";
import { RatingMode } from "@/components/RatingModeSettings";
import { exportCriteriaDescriptionsToPDF } from "@/lib/pdfExport";

interface EvaluationMatrixProps {
  employees: Employee[];
}
interface MatrixData {
  [employeeId: string]: {
    [criteriaKey: string]: CriteriaRating & { sliderValue?: number };
  };
}
const EvaluationMatrix = ({
  employees
}: EvaluationMatrixProps) => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [editEmployeeId, setEditEmployeeId] = useState<string | null>(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [thresholds, setThresholds] = useState<PerformanceThreshold[]>([]);
  const [matrixData, setMatrixData] = useState<MatrixData>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [modifiedEmployees, setModifiedEmployees] = useState<Set<string>>(new Set());
  const [ratingMode, setRatingMode] = useState<RatingMode>(() => {
    const stored = localStorage.getItem("ratingMode");
    // Default to slider mode
    return (stored as RatingMode) || "slider";
  });
  const [editingCriterion, setEditingCriterion] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [confirmResetDialogOpen, setConfirmResetDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  
  // Track if data has been loaded to prevent race conditions
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  // Listen for rating mode changes
  useEffect(() => {
    const handleStorageSync = async () => {
      const stored = localStorage.getItem("ratingMode");
      // Default to slider mode
      setRatingMode((stored as RatingMode) || "slider");
      
      // Reload criteria from database when settings change
      const loadedCriteria = await loadGlobalCriteria();
      setCriteria(loadedCriteria);
    };
    window.addEventListener('storage-sync', handleStorageSync);
    return () => window.removeEventListener('storage-sync', handleStorageSync);
  }, []);
  // Combined data loading effect - prevents race conditions
  useEffect(() => {
    const loadAllData = async () => {
      // Prevent concurrent loads
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      
      try {
        // Load criteria from database (global settings)
        const loadedCriteria = await loadGlobalCriteria();
        setCriteria(loadedCriteria);

        const storedThresholds = localStorage.getItem("performanceThresholds");
        const loadedThresholds = storedThresholds ? JSON.parse(storedThresholds) : getDefaultThresholds();
        setThresholds(loadedThresholds);

        // If editing a specific evaluation, only load that
        if (editId) {
          try {
            const { data: evalData, error: evalError } = await supabase
              .from('evaluations')
              .select('*, employees!inner(*)')
              .eq('id', editId)
              .single();

            if (evalError) throw evalError;

            setMonth(evalData.month);
            setIsEditMode(true);
            setEditEmployeeId(evalData.employee_id);

            // Load criteria ratings
            const { data: ratingsData, error: ratingsError } = await supabase
              .from('criteria_ratings')
              .select('*')
              .eq('evaluation_id', editId);

            if (ratingsError) throw ratingsError;

            // Build matrix data from loaded evaluation
            const employeeId = evalData.employee_id;
            const editData: MatrixData = {};
            editData[employeeId] = {};

            // First initialize with defaults for all criteria
            loadedCriteria.filter((c: Criteria) => c.active).forEach((c: Criteria) => {
              const baseline = getBaselinePoints(c);
              const initialValue = c.criteriaType === 'bonus' ? 0 : baseline;
              editData[employeeId][c.key] = {
                rating: c.criteriaType === 'bonus' ? 'nicht-erfuellt' : 'voll-erfuellt',
                comments: [],
                points: c.defaultPoints,
                sliderValue: initialValue
              };
            });

            // Then override with saved values
            for (const rating of ratingsData || []) {
              const { data: commentsData } = await supabase
                .from('rating_comments')
                .select(`
                  id,
                  text,
                  created_at,
                  profiles!inner(full_name)
                `)
                .eq('rating_id', rating.id)
                .order('created_at', { ascending: true });

              editData[employeeId][rating.criteria_key] = {
                rating: rating.rating as RatingLevel,
                points: rating.points,
                sliderValue: rating.points,
                comments: (commentsData || []).map(c => ({
                  text: c.text,
                  date: c.created_at,
                  userId: '',
                  userName: c.profiles.full_name
                }))
              };
            }

            setMatrixData(editData);
            dataLoadedRef.current = true;
            return;
          } catch (error) {
            console.error('Error loading evaluation:', error);
            toast.error('Fehler beim Laden der Bewertung');
          }
        }

        // For normal matrix view: Load existing evaluations for selected month
        if (employees.length === 0) {
          isLoadingRef.current = false;
          return;
        }

        // Initialize all employees with defaults
        const initialData: MatrixData = {};
        employees.forEach(employee => {
          initialData[employee.id] = {};
          loadedCriteria.filter((c: Criteria) => c.active).forEach((c: Criteria) => {
            const baseline = getBaselinePoints(c);
            const initialValue = c.criteriaType === 'bonus' ? 0 : baseline;
            initialData[employee.id][c.key] = {
              rating: c.criteriaType === 'bonus' ? 'nicht-erfuellt' : 'voll-erfuellt',
              comments: [],
              points: c.defaultPoints,
              sliderValue: initialValue
            };
          });
        });
        
        try {
          // Fetch all evaluations for this month
          const { data: existingEvals, error } = await supabase
            .from('evaluations')
            .select('id, employee_id')
            .eq('month', month);
          
          if (error) throw error;
          
          // Load ratings for each existing evaluation
          for (const evalRecord of existingEvals || []) {
            const { data: ratingsData } = await supabase
              .from('criteria_ratings')
              .select('*')
              .eq('evaluation_id', evalRecord.id);
            
            if (ratingsData && initialData[evalRecord.employee_id]) {
              for (const rating of ratingsData) {
                // Load comments for this rating
                const { data: commentsData } = await supabase
                  .from('rating_comments')
                  .select(`
                    id,
                    text,
                    created_at,
                    profiles!inner(full_name)
                  `)
                  .eq('rating_id', rating.id)
                  .order('created_at', { ascending: true });
                
                initialData[evalRecord.employee_id][rating.criteria_key] = {
                  rating: rating.rating as RatingLevel,
                  points: rating.points,
                  sliderValue: rating.points,
                  comments: (commentsData || []).map(c => ({
                    text: c.text,
                    date: c.created_at,
                    userId: '',
                    userName: c.profiles.full_name
                  }))
                };
              }
            }
          }
        } catch (error) {
          console.error('Error loading existing evaluations:', error);
        }
        
        setMatrixData(initialData);
        dataLoadedRef.current = true;
      } finally {
        isLoadingRef.current = false;
      }
    };
    
    // Reset loaded state when month changes
    dataLoadedRef.current = false;
    loadAllData();
  }, [editId, employees, month]);
  const handleRatingChange = (employeeId: string, criteriaKey: string, rating: RatingLevel) => {
    setModifiedEmployees(prev => new Set(prev).add(employeeId));
    setMatrixData(prev => {
      const employeeData = prev[employeeId] || {};
      const criteriaData = employeeData[criteriaKey] || { comments: [], sliderValue: 0, points: 0, rating: 'nicht-erfuellt' as RatingLevel };
      return {
        ...prev,
        [employeeId]: {
          ...employeeData,
          [criteriaKey]: {
            ...criteriaData,
            rating: criteriaData.rating === rating ? null : rating
          }
        }
      };
    });
  };

  const handleSliderChange = (employeeId: string, criteriaKey: string, value: number, criterion: Criteria) => {
    setModifiedEmployees(prev => new Set(prev).add(employeeId));
    
    const baseline = getBaselinePoints(criterion);
    const maxPoints = getMaxPoints(criterion);
    
    // Convert slider value to rating level for consistency
    let rating: RatingLevel;
    if (criterion.criteriaType === 'bonus') {
      const percentage = maxPoints > 0 ? (value / maxPoints) * 100 : 0;
      if (percentage >= 75) rating = 'voll-erfuellt';
      else if (percentage >= 50) rating = 'erfuellt';
      else if (percentage >= 25) rating = 'teilweise-erfuellt';
      else rating = 'nicht-erfuellt';
    } else {
      if (value >= baseline) rating = 'voll-erfuellt';
      else if (value >= baseline * 0.75) rating = 'erfuellt';
      else if (value >= baseline * 0.5) rating = 'teilweise-erfuellt';
      else rating = 'nicht-erfuellt';
    }
    
    setMatrixData(prev => {
      const employeeData = prev[employeeId] || {};
      const criteriaData = employeeData[criteriaKey] || { comments: [], points: 0, rating: 'nicht-erfuellt' as RatingLevel };
      return {
        ...prev,
        [employeeId]: {
          ...employeeData,
          [criteriaKey]: {
            ...criteriaData,
            rating,
            sliderValue: value
          }
        }
      };
    });
  };
  const handleCommentChange = async (employeeId: string, criteriaKey: string, comment: string) => {
    if (!comment.trim()) return;

    setModifiedEmployees(prev => new Set(prev).add(employeeId));
    const userId = user?.id || "local-user";
    const userName = profile?.full_name || user?.email || "Lokaler Benutzer";
    
    setMatrixData(prev => {
      const employeeData = prev[employeeId] || {};
      const criteriaData = employeeData[criteriaKey] || { comments: [], sliderValue: 0, points: 0, rating: 'nicht-erfuellt' as RatingLevel };
      const existingComments = criteriaData.comments || [];
      return {
        ...prev,
        [employeeId]: {
          ...employeeData,
          [criteriaKey]: {
            ...criteriaData,
            comments: [...existingComments, {
              text: comment,
              date: new Date().toISOString(),
              userId,
              userName
            }]
          }
        }
      };
    });
  };
  const calculateScore = (employeeId: string) => {
    let totalScore = 0;
    let maxScore = 0;
    let baselineScore = 0; // This represents the 100% expectation (ONLY standard criteria)
    let weightedTotalScore = 0;
    let weightedMaxScore = 0;
    let weightedBaselineScore = 0; // Only standard criteria count towards 100%
    
    if (!matrixData[employeeId]) return {
      totalScore: 0,
      maxScore: 0,
      baselineScore: 0,
      weightedTotalScore: 0,
      weightedMaxScore: 0,
      weightedBaselineScore: 0
    };
    
    criteria.forEach(criterion => {
      const rating = matrixData[employeeId][criterion.key];
      if (!rating) return;
      
      const criterionMax = getMaxPoints(criterion);
      const baseline = getBaselinePoints(criterion);
      const weight = criterion.weight || 1;
      
      maxScore += criterionMax;
      weightedMaxScore += criterionMax * weight;
      
      // IMPORTANT: Only STANDARD criteria count towards the 100% baseline
      // Bonus criteria are EXTRA points that can push score ABOVE 100%
      if (criterion.criteriaType === 'bonus') {
        // Bonus criteria do NOT add to baseline - they are extra points
        // baselineScore stays unchanged
        // weightedBaselineScore stays unchanged
      } else {
        // Standard criteria define the 100% expectation
        baselineScore += baseline;
        weightedBaselineScore += baseline * weight;
      }
      
      // Use slider value if in slider mode and sliderValue exists
      if (ratingMode === 'slider' && rating.sliderValue !== undefined) {
        totalScore += rating.sliderValue;
        weightedTotalScore += rating.sliderValue * weight;
      } else {
        // Use checkbox rating mode
        if (rating.rating === 'voll-erfuellt') {
          totalScore += baseline;
          weightedTotalScore += baseline * weight;
        } else if (rating.rating === 'erfuellt') {
          totalScore += baseline * 0.75;
          weightedTotalScore += baseline * 0.75 * weight;
        } else if (rating.rating === 'teilweise-erfuellt') {
          totalScore += baseline * 0.5;
          weightedTotalScore += baseline * 0.5 * weight;
        }
      }
    });
    
    return {
      totalScore,
      maxScore,
      baselineScore,
      weightedTotalScore,
      weightedMaxScore,
      weightedBaselineScore
    };
  };
  const getPerformanceLevel = (score: number, maxScore: number) => {
    return getPerformanceLevelFromThresholds(score, maxScore, thresholds);
  };

  const getIconForCriteria = (key: string) => {
    const iconMap: { [key: string]: any } = {
      punctuality: Clock,
      dailyReport: FileText,
      measurements: Ruler,
      noComplaints: ThumbsUp,
      overtime: Calendar,
      projectDocumentation: FolderCheck,
      costEstimate: Calculator,
      communication: MessageSquare,
      singleSickDays: HeartPulse,
      totalSickDays: Activity,
      specialTasks: ListChecks,
      materialHandling: Wrench,
      vehicleCare: Car,
      projectCompletion: CheckCircle2,
    };
    return iconMap[key];
  };

  const handleEditDescription = (criterion: Criteria) => {
    setEditingCriterion(criterion.id);
    setEditDescription(criterion.description || "");
  };

  const handleSaveDescription = () => {
    if (!editingCriterion) return;
    
    const updatedCriteria = criteria.map(c => 
      c.id === editingCriterion ? { ...c, description: editDescription } : c
    );
    
    // Also update in localStorage with all criteria (active and inactive)
    const storedCriteria = localStorage.getItem("criteria");
    const allCriteria = storedCriteria ? JSON.parse(storedCriteria) : getDefaultCriteria();
    const updatedAllCriteria = allCriteria.map((c: Criteria) => 
      c.id === editingCriterion ? { ...c, description: editDescription } : c
    );
    
    localStorage.setItem("criteria", JSON.stringify(updatedAllCriteria));
    setCriteria(updatedCriteria);
    window.dispatchEvent(new Event('storage-sync'));
    
    setEditingCriterion(null);
    setEditDescription("");
    toast.success("Beschreibung gespeichert");
  };

  const handleSaveSingleEmployee = async (employeeId: string) => {
    if (!user) {
      toast.error("Sie müssen angemeldet sein");
      return;
    }

    try {
      const employee = employees.find(e => e.id === employeeId);
      if (!employee) return;

      const { weightedTotalScore, weightedBaselineScore } = calculateScore(employeeId);
      const performanceLevel = getPerformanceLevel(weightedTotalScore, weightedBaselineScore);

      // Check if evaluation exists
      const { data: existingEval } = await supabase
        .from('evaluations')
        .select('id')
        .eq('employee_id', employeeId)
        .eq('month', month)
        .maybeSingle();

      let evalData;
      
      if (existingEval) {
        // UPDATE
        const { data: updatedEval, error } = await supabase
          .from('evaluations')
          .update({
            total_score: weightedTotalScore,
            max_score: weightedBaselineScore, // Baseline = 100% reference
            performance_level: performanceLevel,
            date: new Date().toISOString()
          })
          .eq('id', existingEval.id)
          .select()
          .single();
        
        if (error) throw error;
        evalData = updatedEval;
        
        // Delete old ratings
        await supabase
          .from('criteria_ratings')
          .delete()
          .eq('evaluation_id', existingEval.id);
      } else {
        // INSERT
        const { data: newEval, error } = await supabase
          .from('evaluations')
          .insert({
            employee_id: employeeId,
            month,
            date: new Date().toISOString(),
            total_score: weightedTotalScore,
            max_score: weightedBaselineScore, // Baseline = 100% reference
            performance_level: performanceLevel,
            created_by: user.id
          })
          .select()
          .single();
        
        if (error) throw error;
        evalData = newEval;
      }

      // Insert criteria ratings
      const criteriaEntries = Object.entries(matrixData[employeeId]).map(([key, rating]) => ({
        evaluation_id: evalData.id,
        criteria_key: key,
        rating: rating.rating,
        // IMPORTANT: persist the actually selected value (so edit shows the same)
        points: rating.sliderValue ?? rating.points
      }));

      await supabase.from('criteria_ratings').insert(criteriaEntries);

      // Insert comments
      for (const [criteriaKey, rating] of Object.entries(matrixData[employeeId])) {
        if (rating.comments?.length > 0) {
          const { data: ratingData } = await supabase
            .from('criteria_ratings')
            .select('id')
            .eq('evaluation_id', evalData.id)
            .eq('criteria_key', criteriaKey)
            .maybeSingle();

          if (ratingData) {
            const comments = rating.comments.map(c => ({
              rating_id: ratingData.id,
              text: c.text,
              created_by: user.id
            }));
            await supabase.from('rating_comments').insert(comments);
          }
        }
      }

      // Remove from modified set
      setModifiedEmployees(prev => {
        const next = new Set(prev);
        next.delete(employeeId);
        return next;
      });

      toast.success(`Bewertung für ${employee.name} gespeichert!`);
    } catch (error) {
      console.error('Error saving employee:', error);
      toast.error('Fehler beim Speichern');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Sie müssen angemeldet sein");
      return;
    }

    const employeesToSave = Array.from(modifiedEmployees);
    
    if (employeesToSave.length === 0) {
      toast.info("Keine Änderungen zum Speichern");
      return;
    }

    try {
      for (const employeeId of employeesToSave) {
        await handleSaveSingleEmployee(employeeId);
      }
      
      toast.success(`${employeesToSave.length} Bewertung(en) gespeichert!`);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error saving evaluations:', error);
      toast.error("Fehler beim Speichern");
    }
  };
  if (employees.length === 0) {
    return <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">
            Keine Mitarbeiter vorhanden. Bitte fügen Sie zunächst Mitarbeiter hinzu.
          </p>
        </CardContent>
      </Card>;
  }
  const handleExportCriteriaDescriptions = async () => {
    try {
      // Always load the latest criteria from database to ensure PDF is up-to-date
      const { data: dbData, error: dbError } = await supabase
        .from('evaluation_criteria_settings')
        .select('criteria')
        .eq('id', 'global')
        .maybeSingle();
      
      let allCriteria: Criteria[];
      
      if (!dbError && dbData?.criteria && Array.isArray(dbData.criteria) && dbData.criteria.length > 0) {
        // Use database criteria (includes all criteria, not just active ones)
        allCriteria = dbData.criteria as unknown as Criteria[];
      } else {
        // Fallback to localStorage or defaults
        const storedCriteria = localStorage.getItem("criteria");
        allCriteria = storedCriteria ? JSON.parse(storedCriteria) : getDefaultCriteria();
      }
      
      await exportCriteriaDescriptionsToPDF(allCriteria, {
        createdByName: profile?.full_name || user?.email
      });
      toast.success("PDF erfolgreich erstellt!");
    } catch (error) {
      console.error("Error exporting criteria descriptions:", error);
      toast.error("Fehler beim PDF-Export");
    }
  };

  // Handle monthly reset - resets all evaluations to defaults for the current month
  const handleMonthlyReset = async () => {
    if (!user) {
      toast.error("Sie müssen angemeldet sein");
      return;
    }

    setIsResetting(true);
    try {
      // The evaluations remain in the database as history!
      // We just reset the UI to defaults for a fresh start
      
      // Delete current month's evaluations (they're archived in evaluation_history via trigger)
      const { data: existingEvals, error: fetchError } = await supabase
        .from('evaluations')
        .select('id')
        .eq('month', month);
      
      if (fetchError) throw fetchError;
      
      if (existingEvals && existingEvals.length > 0) {
        // Delete ratings first (cascade should handle this, but being explicit)
        for (const eval_ of existingEvals) {
          await supabase
            .from('criteria_ratings')
            .delete()
            .eq('evaluation_id', eval_.id);
        }
        
        // Delete evaluations
        const { error: deleteError } = await supabase
          .from('evaluations')
          .delete()
          .eq('month', month);
        
        if (deleteError) throw deleteError;
      }
      
      // Reset local matrix to defaults
      const initialData: MatrixData = {};
      employees.forEach(employee => {
        initialData[employee.id] = {};
        criteria.filter((c: Criteria) => c.active).forEach((c: Criteria) => {
          const baseline = getBaselinePoints(c);
          const initialValue = c.criteriaType === 'bonus' ? 0 : baseline;
          initialData[employee.id][c.key] = {
            rating: c.criteriaType === 'bonus' ? 'nicht-erfuellt' : 'voll-erfuellt',
            comments: [],
            points: c.defaultPoints,
            sliderValue: initialValue
          };
        });
      });
      
      setMatrixData(initialData);
      setModifiedEmployees(new Set());
      setConfirmResetDialogOpen(false);
      setResetDialogOpen(false);
      
      toast.success(`Bewertungen für ${month} wurden zurückgesetzt. Die Historie bleibt erhalten.`);
    } catch (error) {
      console.error('Error resetting evaluations:', error);
      toast.error("Fehler beim Zurücksetzen");
    } finally {
      setIsResetting(false);
    }
  };

  // Filter employees: in edit mode, show only the employee being edited
  const displayedEmployees = isEditMode && editEmployeeId
    ? employees.filter(e => e.id === editEmployeeId)
    : employees;

  return <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg sm:text-xl">{isEditMode ? 'Bewertung bearbeiten' : 'Bewertungszeitraum'}</CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCriteriaDescriptions}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">Betriebsanweisungen</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Kriterien als Betriebsanweisungen (PDF) exportieren</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="space-y-2">
              <Label htmlFor="month">Monat</Label>
              <Input 
                id="month" 
                type="month" 
                value={month} 
                onChange={e => setMonth(e.target.value)} 
                required 
                className="w-full max-w-xs"
                disabled={isEditMode}
              />
            </div>
            
            {!isEditMode && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setResetDialogOpen(true)}
                      className="gap-2 text-destructive hover:text-destructive"
                    >
                      <RotateCcw className="h-4 w-4" />
                      <span className="hidden sm:inline">Monat zurücksetzen</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Alle Bewertungen für diesen Monat auf Standardwerte zurücksetzen</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Bewertungsmatrix</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DualScrollbar enableKeyboardNavigation scrollStep={300}>
              <div className="min-w-max p-3 sm:p-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="sticky left-0 z-20 p-2 text-left font-semibold w-[100px] min-w-[100px] max-w-[100px] bg-card text-xs">Kriterium</th>
                      {displayedEmployees.map(employee => <th key={employee.id} className="p-2 text-center font-semibold w-[120px] border-l bg-card">
                          <div className="space-y-2">
                            <div 
                              className="space-y-1 cursor-pointer hover:bg-accent/50 transition-colors p-2 -m-2 rounded"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/employee/${employee.id}`);
                              }}
                            >
                              <div className="flex items-center justify-center gap-2">
                                {employee.name}
                                {modifiedEmployees.has(employee.id) && (
                                  <Badge variant="outline" className="text-xs">
                                    Ungespeichert
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs font-normal text-muted-foreground">
                                {employee.position}
                              </div>
                            </div>
                            
                            <Button
                              type="button"
                              size="sm"
                              variant={modifiedEmployees.has(employee.id) ? "default" : "outline"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveSingleEmployee(employee.id);
                              }}
                              disabled={!modifiedEmployees.has(employee.id)}
                              className="w-full"
                            >
                              <Save className="h-3 w-3 mr-1" />
                              Speichern
                            </Button>
                          </div>
                        </th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {criteria.map((criterion, critIndex) => {
                      const CriterionIcon = getIconForCriteria(criterion.key);
                      return <tr key={criterion.id} className={critIndex % 2 === 0 ? "bg-muted/30" : ""}>
                        <td className="sticky left-0 bg-background z-20 p-2 border-b font-medium w-[100px] min-w-[100px] max-w-[100px]">
                          <div className="flex items-start gap-1">
                            {CriterionIcon && <CriterionIcon className="h-3 w-3 mt-0.5 flex-shrink-0 text-primary" />}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs leading-tight flex items-start gap-0.5 flex-wrap">
                                <span className="break-words hyphens-auto" style={{ wordBreak: 'break-word' }}>
                                  {criterion.label}
                                </span>
                                <TooltipProvider delayDuration={100}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-4 w-4 p-0 flex-shrink-0"
                                        onClick={() => handleEditDescription(criterion)}
                                      >
                                        <Info className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                      side="right" 
                                      className="max-w-[280px] sm:max-w-sm p-3"
                                      sideOffset={5}
                                    >
                                      <div className="space-y-2">
                                        <p className="font-semibold text-sm text-foreground">{criterion.label}</p>
                                        <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                                          {criterion.description || "Keine Beschreibung vorhanden. Klicken Sie auf das Info-Symbol, um eine Beschreibung hinzuzufügen."}
                                        </p>
                                        <p className="text-xs text-muted-foreground/70 pt-1 border-t border-border">
                                          Max. Punkte: {criterion.defaultPoints} | Gewichtung: {criterion.weight || 1}x
                                        </p>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">{criterion.defaultPoints} Pkt.</div>
                            </div>
                          </div>
                        </td>
                        {displayedEmployees.map(employee => <td key={employee.id} className="p-4 border-b border-l align-top">
                            <div className="space-y-3">
                              {ratingMode === 'slider' ? (
                                <BidirectionalSlider
                                  value={matrixData[employee.id]?.[criterion.key]?.sliderValue ?? (criterion.criteriaType === 'bonus' ? 0 : getBaselinePoints(criterion))}
                                  minValue={getMinPoints(criterion)}
                                  maxValue={getMaxPoints(criterion)}
                                  baselineValue={getBaselinePoints(criterion)}
                                  criteriaType={criterion.criteriaType || 'standard'}
                                  weight={criterion.weight || 1}
                                  onChange={(value) => handleSliderChange(employee.id, criterion.key, value, criterion)}
                                />
                              ) : (
                                <div className="grid grid-cols-2 gap-2">
                                  {ratingOptions.map(({
                                    value,
                                    label
                                  }) => <div key={value} className="flex items-center space-x-2">
                                        <Checkbox id={`${employee.id}-${criterion.key}-${value}`} checked={matrixData[employee.id]?.[criterion.key]?.rating === value} onCheckedChange={() => handleRatingChange(employee.id, criterion.key, value as RatingLevel)} />
                                        <Label htmlFor={`${employee.id}-${criterion.key}-${value}`} className="text-xs cursor-pointer">
                                          {label}
                                        </Label>
                                      </div>)}
                                </div>
                              )}
                              <div className="space-y-2">
                                {matrixData[employee.id]?.[criterion.key]?.comments?.map((comment, idx) => <div key={idx} className="text-xs p-2 bg-muted rounded border">
                                    <div className="flex justify-between items-start mb-1">
                                      <div className="font-medium text-muted-foreground">
                                        {new Date(comment.date).toLocaleDateString('de-DE')}
                                      </div>
                                      <div className="text-xs text-primary">
                                        {comment.userName}
                                      </div>
                                    </div>
                                    <div>{comment.text}</div>
                                  </div>)}
                                <Textarea placeholder="Neuen Kommentar hinzufügen..." onBlur={e => {
                            if (e.target.value.trim()) {
                              handleCommentChange(employee.id, criterion.key, e.target.value);
                              e.target.value = '';
                            }
                          }} onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              const target = e.target as HTMLTextAreaElement;
                              if (target.value.trim()) {
                                handleCommentChange(employee.id, criterion.key, target.value);
                                target.value = '';
                              }
                            }
                          }} rows={2} className="text-xs" />
                              </div>
                            </div>
                          </td>)}
                      </tr>;
                    })}
                  </tbody>
                </table>
              </div>
            </DualScrollbar>
          </CardContent>
        </Card>

        <Card className="bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle>Zusammenfassung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {displayedEmployees.map(employee => {
              const {
                totalScore,
                maxScore,
                baselineScore,
                weightedTotalScore,
                weightedMaxScore,
                weightedBaselineScore
              } = calculateScore(employee.id);
              // Calculate percentage based on baseline (100% = meeting all expectations)
              const percentageOfBaseline = weightedBaselineScore > 0 
                ? (weightedTotalScore / weightedBaselineScore) * 100 
                : 0;
              // Use weightedBaselineScore as the 100% reference, so bonus points can push above 100%
              const performanceLevel = getPerformanceLevel(weightedTotalScore, weightedBaselineScore);
              return <div key={employee.id} className="flex items-center justify-between p-3 bg-card rounded-lg">
                    <span className="font-medium">{employee.name}</span>
                    <div className="flex gap-4 items-center">
                      <span className="text-sm text-muted-foreground">
                        {weightedTotalScore.toFixed(1)} / {weightedBaselineScore} Soll-Pkt.
                      </span>
                      <span className={`font-bold ${percentageOfBaseline > 100 ? 'text-green-500' : percentageOfBaseline >= 75 ? 'text-primary' : 'text-orange-500'}`}>
                        {percentageOfBaseline.toFixed(0)}%
                      </span>
                      <Badge variant={percentageOfBaseline > 100 ? 'default' : 'secondary'} className={percentageOfBaseline > 100 ? 'bg-green-500/20 text-green-500 border-green-500/30' : ''}>
                        {performanceLevel}
                      </Badge>
                    </div>
                  </div>;
            })}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between items-center">
          <div className="text-sm text-muted-foreground">
            {modifiedEmployees.size > 0 ? (
              <>
                <span className="font-medium text-primary">
                  {modifiedEmployees.size}
                </span> Mitarbeiter mit ungespeicherten Änderungen
              </>
            ) : (
              "Keine ungespeicherten Änderungen"
            )}
          </div>
          
          <Button 
            type="submit" 
            className="gap-2"
            disabled={modifiedEmployees.size === 0}
          >
            <Save className="h-4 w-4" />
            Alle Änderungen speichern ({modifiedEmployees.size})
          </Button>
        </div>
      </form>

      {/* Edit Description Dialog */}
      <Dialog open={!!editingCriterion} onOpenChange={(open) => !open && setEditingCriterion(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Kriterienbeschreibung bearbeiten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Kriterium: <span className="font-medium text-foreground">
                {criteria.find(c => c.id === editingCriterion)?.label}
              </span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Beschreiben Sie, was dieses Kriterium bewertet. Diese Information wird den Mitarbeitern angezeigt."
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Diese Erklärung wird den Mitarbeitern über das Info-Icon angezeigt.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCriterion(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveDescription} className="gap-2">
              <Check className="h-4 w-4" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* First Reset Confirmation Dialog */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Monat zurücksetzen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sie sind dabei, alle Bewertungen für <strong>{month}</strong> zurückzusetzen. 
              Die bisherigen Werte werden auf die Standardeinstellungen zurückgesetzt.
              <br /><br />
              <span className="text-muted-foreground">
                Die Historie bleibt erhalten und kann in den Mitarbeiterdetails eingesehen werden.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setResetDialogOpen(false);
                setConfirmResetDialogOpen(true);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Weiter zur Bestätigung
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Second (Final) Reset Confirmation Dialog */}
      <AlertDialog open={confirmResetDialogOpen} onOpenChange={setConfirmResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Endgültige Bestätigung
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                <strong>Sind Sie sicher?</strong> Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
              <p>
                Alle aktuellen Bewertungswerte für den Monat <strong>{month}</strong> werden gelöscht 
                und auf die Standardwerte zurückgesetzt.
              </p>
              <p className="text-xs text-muted-foreground pt-2">
                Hinweis: Die Bewertungshistorie bleibt in den Mitarbeiterakten erhalten.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMonthlyReset}
              disabled={isResetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isResetting ? "Wird zurückgesetzt..." : "Ja, endgültig zurücksetzen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};
export default EvaluationMatrix;